import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../services/firebase';
import apiService from '../services/api';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';

const API = 'https://tradebot-k4nt.onrender.com/api';

// Login:  POST /api/login   { pin, telegrameUsername }  → status 200
// Signup: POST /api/signUp  { ref, pin, telegrameUsername } → status 201
// Google: POST /api/google-log { telegrameUsername, email, pin: '' } → status 201

const AuthPage = () => {
  const navigate = useNavigate();
  const { user: contextUser, login: contextLogin, setUserFromData } = useUser();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [loading, setLoading] = useState(false);

  // Login state — step 1 = username, step 2 = pin
  const [loginStep, setLoginStep] = useState('username');
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPin, setLoginPin] = useState('');
  const [showLoginPin, setShowLoginPin] = useState(false);

  // Signup state
  const [signStep, setSignStep] = useState('username');
  const [signUsername, setSignUsername] = useState('');
  const [signPin, setSignPin] = useState('');
  const [signPinConfirm, setSignPinConfirm] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [showSignPin, setShowSignPin] = useState(false);

  const formatUsername = (name) => {
    const clean = name.trim().replace(/^@/, '');
    return `@${clean}`;
  };

  // Step 1: submit username to reveal PIN field
  // Step 2: POST /api/login
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginUsername.trim()) { toast.error('Username is required'); return; }

    if (loginStep === 'username') {
      setLoginStep('pin');
      return;
    }

    if (!loginPin || loginPin.length < 4) { toast.error('Enter your PIN'); return; }

    setLoading(true);
    try {
      const result = await contextLogin(loginUsername.trim().replace(/^@/, ''), loginPin);
      if (result.success) {
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Invalid username or PIN');
        setLoginStep('username');
        setLoginPin('');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: submit username to reveal PIN fields
  // Step 2: POST /api/signUp with ref from URL
  const handleSignup = async (e) => {
    e.preventDefault();
    if (!signUsername.trim()) { toast.error('Username is required'); return; }

    if (signStep === 'username') {
      setSignStep('pin');
      return;
    }

    if (!signPin || signPin.length < 4) { toast.error('Enter a PIN (min 4 digits)'); return; }
    if (signPin !== signPinConfirm) { toast.error('PINs do not match'); return; }
    if (!agreed) { toast.error('Please agree to the terms of service'); return; }

    setLoading(true);
    try {
      const ref = new URLSearchParams(window.location.search).get('ref') || null;
      const response = await fetch(`${API}/signUp`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ref,
          pin: signPin,
          telegrameUsername: signUsername.trim().replace(/^@/, '')
        })
      });
      const data = await response.json();

      if (response.status === 201) {
        const userData = { ...data?.usernameInfo, pin: signPin };
        setUserFromData(userData);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        toast.error(data?.error || 'Signup failed. Username may already exist.');
        setSignStep('username');
        setSignPin('');
        setSignPinConfirm('');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Google sign-in → POST /api/google-log
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account', display: 'popup' });
      provider.addScope('profile');
      provider.addScope('email');

      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;

      if (!googleUser) throw new Error('No user data received from Google');

      const telegramName = googleUser.displayName?.replace(/\s+/g, '') || '';
      const email = googleUser.email || '';

      if (!telegramName) {
        toast.error('Unable to get your name from Google account');
        setLoading(false);
        return;
      }

      const formattedUsername = formatUsername(telegramName);
      const response = await apiService.googleLogin(formattedUsername, email);

      if (response.status === 201 && response.data.usernameInfo) {
        const userData = response.data.usernameInfo;
        setUserFromData(userData);
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        toast.error('Failed to authenticate with Google');
      }
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        toast.info('Login was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup was blocked by your browser. Please allow popups for this site.');
      } else if (error.code === 'auth/network-request-failed') {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        toast.error('This domain is not authorized for Google login. Please contact support.');
      } else {
        toast.error(error.response?.data?.error || error.message || 'Google login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setLoginStep('username');
    setSignStep('username');
    setLoginUsername('');
    setLoginPin('');
    setSignUsername('');
    setSignPin('');
    setSignPinConfirm('');
    setAgreed(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">MODANMIC</h1>
          <p className="text-slate-600">
            {mode === 'login' ? 'Welcome back!' : 'Create your account'}
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-white rounded-xl p-1 mb-6 shadow-sm border border-slate-200">
          {['login', 'signup'].map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => switchMode(m)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                mode === m
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {m === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">
                    {loginStep === 'username' ? 'Enter your username' : 'Enter your PIN'}
                  </h2>
                  <p className="text-sm text-slate-500 mb-4">
                    {loginStep === 'username'
                      ? 'Type your MODANMIC username to continue'
                      : `Signing in as @${loginUsername}`}
                  </p>
                </div>

                {/* Username */}
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-slate-700 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-slate-500 text-sm">@</span>
                    </div>
                    <input
                      id="username"
                      type="text"
                      placeholder="your_username"
                      value={loginUsername}
                      onChange={(e) => setLoginUsername(e.target.value.replace(/^@/, ''))}
                      className="w-full pl-8 pr-14 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      required
                      autoComplete="off"
                      readOnly={loginStep === 'pin'}
                    />
                    {loginStep === 'pin' && (
                      <button
                        type="button"
                        onClick={() => { setLoginStep('username'); setLoginPin(''); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>

                {/* PIN — revealed after username step */}
                {loginStep === 'pin' && (
                  <div>
                    <label htmlFor="pin" className="block text-sm font-medium text-slate-700 mb-2">
                      PIN
                    </label>
                    <div className="relative">
                      <input
                        id="pin"
                        type={showLoginPin ? 'text' : 'password'}
                        placeholder="6 digit PIN"
                        value={loginPin}
                        onChange={(e) => setLoginPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className="w-full px-4 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        required
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPin(!showLoginPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showLoginPin ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <button
                  id="log-acc-butt"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    loginStep === 'username' ? 'Continue →' : 'Sign In'
                  )}
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-slate-500">Or continue with</span>
                  </div>
                </div>

                {/* Google Login */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google"
                    className="w-5 h-5"
                  />
                  {loading ? 'Connecting...' : 'Continue with Google'}
                </button>
              </div>
            </form>
          )}

          {/* SIGNUP FORM */}
          {mode === 'signup' && (
            <form onSubmit={handleSignup}>
              <div className="space-y-5">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">
                    {signStep === 'username' ? 'Choose a username' : 'Set your PIN'}
                  </h2>
                  <p className="text-sm text-slate-500 mb-4">
                    {signStep === 'username'
                      ? 'Pick a unique username for your account'
                      : `Username: @${signUsername}`}
                  </p>
                </div>

                {/* Username */}
                <div>
                  <label htmlFor="usernameSigne" className="block text-sm font-medium text-slate-700 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-slate-500 text-sm">@</span>
                    </div>
                    <input
                      id="usernameSigne"
                      type="text"
                      placeholder="choose_username"
                      value={signUsername}
                      onChange={(e) => setSignUsername(e.target.value.replace(/^@/, '').replace(/\s/g, '_'))}
                      className="w-full pl-8 pr-14 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      required
                      autoComplete="off"
                      readOnly={signStep === 'pin'}
                    />
                    {signStep === 'pin' && (
                      <button
                        type="button"
                        onClick={() => { setSignStep('username'); setSignPin(''); setSignPinConfirm(''); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">3-20 characters (letters, numbers, underscores)</p>
                </div>

                {/* PIN + Confirm PIN — revealed after username step */}
                {signStep === 'pin' && (
                  <>
                    <div>
                      <label htmlFor="pinSigne" className="block text-sm font-medium text-slate-700 mb-2">
                        PIN
                      </label>
                      <div className="relative">
                        <input
                          id="pinSigne"
                          type={showSignPin ? 'text' : 'password'}
                          placeholder="Create a 6 digit PIN"
                          value={signPin}
                          onChange={(e) => setSignPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          className="w-full px-4 pr-12 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                          required
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignPin(!showSignPin)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showSignPin ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm PIN */}
                    <div>
                      <label htmlFor="creatrpinSigne" className="block text-sm font-medium text-slate-700 mb-2">
                        Confirm PIN
                      </label>
                      <input
                        id="creatrpinSigne"
                        type="password"
                        placeholder="Repeat your PIN"
                        value={signPinConfirm}
                        onChange={(e) => setSignPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
                          signPinConfirm && signPin !== signPinConfirm ? 'border-red-400' : 'border-slate-300'
                        }`}
                        required
                      />
                      {signPinConfirm && signPin !== signPinConfirm && (
                        <p className="text-red-500 text-xs mt-1">PINs do not match</p>
                      )}
                    </div>

                    {/* Agreement */}
                    <div className="flex items-start gap-2">
                      <input
                        id="confarmArgrement"
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        required
                      />
                      <label htmlFor="confarmArgrement" className="text-sm text-slate-600">
                        I agree to MODANMIC's Terms of Service and Privacy Policy
                      </label>
                    </div>
                  </>
                )}

                {/* Submit */}
                <button
                  id="sig-acc-butt"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </span>
                  ) : (
                    signStep === 'username' ? 'Continue →' : 'Create Account'
                  )}
                </button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-slate-500">Or sign up with</span>
                  </div>
                </div>

                {/* Google Signup */}
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google"
                    className="w-5 h-5"
                  />
                  {loading ? 'Connecting...' : 'Continue with Google'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Toggle Login/Signup */}
        <div className="text-center mt-6">
          <p className="text-sm text-slate-600">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              type="button"
              onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>

        {/* Back to home */}
        <div className="text-center mt-3">
          <button
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-slate-900 text-sm font-medium"
          >
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
